'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import {
  ChevronDown,
  ChevronRight,
  CheckCircle2,
  Circle,
  ExternalLink,
  Award,
} from 'lucide-react';
import { WeekData, PHASE_META, DAY_SHORT, DAY_LABELS } from '@/lib/curriculum';
import { WeekProgress, toggleDay, toggleAssessment } from '@/lib/storage';

interface WeekAccordionCardProps {
  week: WeekData;
  weekProgress: WeekProgress | null;
  isExpanded: boolean;
  status: 'complete' | 'in-progress' | 'not-started';
  onToggleExpand: () => void;
  onProgressChange: () => void;
}

export default function WeekAccordionCard({
  week,
  weekProgress,
  isExpanded,
  status,
  onToggleExpand,
  onProgressChange,
}: WeekAccordionCardProps) {
  const phase = PHASE_META[week.phase];
  const daysComplete = weekProgress?.daysComplete ?? [false, false, false, false, false];
  const assessmentPassed = weekProgress?.assessmentPassed ?? false;
  const completedCount = daysComplete.filter(Boolean).length;

  const [isHovered, setIsHovered] = useState(false);

  const handleToggleDay = (e: React.MouseEvent, dayIdx: number) => {
    e.stopPropagation();
    toggleDay(week.weekNum, dayIdx);
    onProgressChange();
  };

  const handleToggleAssessment = (e: React.MouseEvent) => {
    e.stopPropagation();
    toggleAssessment(week.weekNum);
    onProgressChange();
  };

  const statusColors = {
    complete: { bg: 'var(--accent-glow)', border: 'var(--accent-border)', dot: 'var(--accent)' },
    'in-progress': { bg: 'rgba(91, 168, 245, 0.08)', border: 'rgba(91, 168, 245, 0.3)', dot: '#5BA8F5' },
    'not-started': { bg: 'transparent', border: 'var(--border)', dot: 'var(--text-muted)' },
  };

  const sc = statusColors[status];

  return (
    <div
      style={{
        backgroundColor: isHovered && !isExpanded ? 'var(--bg-card-hover)' : 'var(--bg-card)',
        border: `1px solid ${isExpanded ? 'var(--accent-border)' : sc.border}`,
        borderRadius: '10px',
        overflow: 'hidden',
        transition: 'all 150ms ease',
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Header row */}
      <button
        onClick={onToggleExpand}
        aria-expanded={isExpanded}
        aria-label={`Week ${week.weekNum}: ${week.title}`}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          padding: '14px 16px',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          textAlign: 'left',
        }}
      >
        {/* Status dot */}
        <div
          style={{
            width: '8px',
            height: '8px',
            borderRadius: '50%',
            backgroundColor: sc.dot,
            flexShrink: 0,
          }}
        />

        {/* Week number */}
        <span
          style={{
            fontFamily: 'IBM Plex Mono, monospace',
            fontSize: '12px',
            fontWeight: 600,
            color: 'var(--text-muted)',
            width: '44px',
            flexShrink: 0,
          }}
        >
          WK {week.weekNum.toString().padStart(2, '0')}
        </span>

        {/* Phase badge */}
        <span
          style={{
            fontSize: '11px',
            padding: '2px 8px',
            borderRadius: '4px',
            backgroundColor: phase.color,
            color: phase.textColor,
            border: `1px solid ${phase.border}`,
            flexShrink: 0,
            fontWeight: 500,
          }}
        >
          {phase.label}
        </span>

        {/* Title */}
        <span
          style={{
            flex: 1,
            fontSize: '14px',
            fontWeight: 500,
            color: 'var(--text-primary)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {week.title}
        </span>

        {/* Progress mini */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
          <div style={{ display: 'flex', gap: '3px' }}>
            {daysComplete.map((done, idx) => (
              <div
                key={`mini-day-${week.weekNum}-${idx}`}
                style={{
                  width: '6px',
                  height: '6px',
                  borderRadius: '2px',
                  backgroundColor: done ? 'var(--accent)' : 'var(--bg-elevated)',
                  border: `1px solid ${done ? 'var(--accent-border)' : 'var(--border)'}`,
                }}
              />
            ))}
          </div>
          {assessmentPassed && (
            <Award size={13} style={{ color: 'var(--accent)' }} />
          )}
        </div>

        {/* Chevron */}
        <div style={{ color: 'var(--text-muted)', flexShrink: 0, marginLeft: '4px' }}>
          {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        </div>
      </button>

      {/* Expanded content */}
      {isExpanded && (
        <div
          style={{
            borderTop: '1px solid var(--border)',
            padding: '16px',
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
            animation: 'fadeIn 150ms ease forwards',
          }}
        >
          {/* Mon–Fri columns */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(5, 1fr)',
              gap: '8px',
            }}
          >
            {week.days.map((day, dayIdx) => {
              const done = daysComplete[dayIdx];
              return (
                <div
                  key={`day-col-${week.weekNum}-${dayIdx}`}
                  style={{
                    backgroundColor: done ? 'var(--accent-glow)' : 'var(--bg-elevated)',
                    border: `1px solid ${done ? 'var(--accent-border)' : 'var(--border-subtle)'}`,
                    borderRadius: '8px',
                    padding: '10px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '8px',
                    transition: 'all 150ms ease',
                  }}
                >
                  {/* Day label + checkbox */}
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span
                      style={{
                        fontSize: '11px',
                        fontFamily: 'IBM Plex Mono, monospace',
                        fontWeight: 600,
                        color: done ? 'var(--accent)' : 'var(--text-muted)',
                        letterSpacing: '0.04em',
                      }}
                    >
                      {DAY_SHORT[dayIdx].toUpperCase()}
                    </span>
                    <button
                      onClick={e => handleToggleDay(e, dayIdx)}
                      aria-label={`Mark ${DAY_LABELS[dayIdx]} ${done ? 'incomplete' : 'complete'}`}
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        padding: '2px',
                        color: done ? 'var(--accent)' : 'var(--text-muted)',
                        display: 'flex',
                        transition: 'color 150ms ease',
                      }}
                    >
                      {done ? <CheckCircle2 size={16} /> : <Circle size={16} />}
                    </button>
                  </div>

                  {/* Topic */}
                  <div
                    style={{
                      fontSize: '12px',
                      fontWeight: 600,
                      color: 'var(--text-primary)',
                      lineHeight: 1.3,
                    }}
                  >
                    {day.topic}
                  </div>

                  {/* Task */}
                  <div
                    style={{
                      fontSize: '11px',
                      color: 'var(--text-muted)',
                      lineHeight: 1.4,
                    }}
                  >
                    {day.task}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Assessment row */}
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '12px',
              padding: '12px 14px',
              backgroundColor: assessmentPassed ? 'var(--accent-glow)' : 'var(--bg-elevated)',
              border: `1px solid ${assessmentPassed ? 'var(--accent-border)' : 'var(--border-subtle)'}`,
              borderRadius: '8px',
            }}
          >
            <Award
              size={16}
              style={{ color: assessmentPassed ? 'var(--accent)' : 'var(--text-muted)', marginTop: '1px', flexShrink: 0 }}
            />
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '4px', fontFamily: 'IBM Plex Mono, monospace', letterSpacing: '0.04em' }}>
                WEEKLY ASSESSMENT
              </div>
              <div style={{ fontSize: '13px', color: 'var(--text-primary)', lineHeight: 1.4 }}>
                {week.assessment}
              </div>
            </div>
            <button
              onClick={handleToggleAssessment}
              aria-label={assessmentPassed ? 'Mark assessment as not passed' : 'Mark assessment as passed'}
              style={{
                padding: '6px 12px',
                borderRadius: '6px',
                border: `1px solid ${assessmentPassed ? 'var(--accent-border)' : 'var(--border)'}`,
                backgroundColor: assessmentPassed ? 'var(--accent)' : 'var(--bg-card)',
                color: assessmentPassed ? '#fff' : 'var(--text-secondary)',
                cursor: 'pointer',
                fontSize: '12px',
                fontWeight: 600,
                transition: 'all 150ms ease',
                whiteSpace: 'nowrap',
                flexShrink: 0,
              }}
            >
              {assessmentPassed ? '✓ Passed' : 'Mark Passed'}
            </button>
          </div>

          {/* Actions row */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ fontSize: '12px', color: 'var(--text-muted)', fontFamily: 'IBM Plex Mono, monospace' }}>
              {completedCount}/5 days · {assessmentPassed ? 'Assessment passed' : 'Assessment pending'}
            </div>
            <Link
              href={`/week-detail-view?week=${week.weekNum}`}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '6px 12px',
                borderRadius: '6px',
                border: '1px solid var(--border)',
                backgroundColor: 'var(--bg-elevated)',
                color: 'var(--text-secondary)',
                textDecoration: 'none',
                fontSize: '13px',
                fontWeight: 500,
                transition: 'all 150ms ease',
              }}
              onMouseEnter={e => {
                (e.currentTarget as HTMLElement).style.borderColor = 'var(--accent-border)';
                (e.currentTarget as HTMLElement).style.color = 'var(--accent)';
              }}
              onMouseLeave={e => {
                (e.currentTarget as HTMLElement).style.borderColor = 'var(--border)';
                (e.currentTarget as HTMLElement).style.color = 'var(--text-secondary)';
              }}
            >
              <ExternalLink size={13} />
              Open Week
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}